/**
 * The "Your first release" checklist (spec 2026-10-08 onboarding §4.5). Each artist has its own
 * checklist (owner rule 2026-10-09). music24-api computes it on every read of `onboarding.state`; web, iOS, Android and the activation emails show it. Items 2, 3
 * and 8 come from `releaseSetupCheck`, so the release page and the checklist always agree.
 * The file imports only types.
 */
import type { Music24PlanTier } from './music24Plans';
import type { ReleaseSetup, ReleaseSetupKey, ReleaseSongSection } from './releases';

/** Every checklist item key, in the order of the checklist. */
export const ONBOARDING_ITEM_KEYS = [
  'song',
  'audio',
  'lyrics',
  'look',
  'kit',
  'channel',
  'plan',
  'link',
  'streams',
  'planPick',
] as const;

export type OnboardingItemKey = (typeof ONBOARDING_ITEM_KEYS)[number];

/**
 * Where an item's button goes. Each client maps it to its own screen.
 * `accountId` is the artist of the checklist: the screen opens under that artist. It is null only
 * when the workspace has no artist yet.
 */
export type OnboardingAction =
  | { screen: 'addSong'; accountId: number | null }
  | { screen: 'songPage'; catalogItemUuid: string; section: ReleaseSongSection; accountId: number }
  | { screen: 'releaseTheme' | 'releaseAssets' | 'releasePlan'; catalogItemUuid: string; accountId: number }
  | { screen: 'channels'; accountId: number | null }
  | { screen: 'catalogConnect'; accountId: number | null }
  | { screen: 'plans'; accountId: number | null };

/**
 * A soft reminder on an item that is done but misses an optional part. Today only `cover`: the
 * first-release song has no cover (owner answer 4, 2026-10-09: the cover is optional everywhere,
 * and the checklist still nudges to add one). A nudge never changes `done`, the count or the keys,
 * so the analytics and the activation emails stay the same. Clients that do not know the field
 * ignore it (the native decoders ignore unknown keys).
 */
export type OnboardingNudge = { kind: 'cover'; catalogItemUuid: string; accountId: number };

export interface OnboardingChecklistItem {
  key: OnboardingItemKey;
  done: boolean;
  action: OnboardingAction;
  /** Set only while the nudge applies. */
  nudge?: OnboardingNudge;
}

export interface OnboardingChecklistFacts {
  /** The artist of the checklist, or null when the workspace has no artist. */
  accountId: number | null;
  /** The first-release song of the artist, or null when the artist has none. */
  song: {
    catalogItemUuid: string;
    /** The artist of the song: the artist of the checklist. */
    accountId: number;
    setup: ReleaseSetup;
    /** `ReleaseKitModel.lookChosenAt` is set. */
    lookChosen: boolean;
    /** Ready, current kit assets of the song. */
    readyKitAssets: number;
    /** Rollout drafts and scheduled posts of the song (not canceled). */
    plannedPosts: number;
  } | null;
  /** Social channels with status `connected` of the artist. A channel belongs to one artist. */
  connectedChannels: number;
  /** Artist-portal or distributor logins of the artist that name an artist. */
  streamSources: number;
  plan: Music24PlanTier;
}

export interface OnboardingChecklist {
  items: OnboardingChecklistItem[];
  doneCount: number;
  total: number;
  complete: boolean;
}

/** A paid plan: every tier but Free and the trial. */
export function isPaidTier(tier: Music24PlanTier): boolean {
  return tier !== 'free' && tier !== 'trial';
}

/** True when the song has a cover: the cover-and-date item names no missing cover. */
function setupHasCover(setup: ReleaseSetup): boolean {
  const item = setup.items.find((entry) => entry.key === 'coverAndDate');
  return item === undefined || (item.detail !== 'noCover' && item.detail !== 'noCoverNoDate');
}

function setupItemDone(setup: ReleaseSetup, key: ReleaseSetupKey): boolean {
  const item = setup.items.find((entry) => entry.key === key);
  // An item that does not apply (the pre-save of a released song) counts as done.
  return item === undefined || item.state === 'ok' || item.state === 'working';
}

/** The `song` item: done once a song is added; a song with no cover carries the cover nudge. */
function songItem(song: OnboardingChecklistFacts['song'], addSong: OnboardingAction): OnboardingChecklistItem {
  if (song === null) {
    return { key: 'song', done: false, action: addSong };
  }
  if (setupHasCover(song.setup)) {
    return { key: 'song', done: true, action: addSong };
  }
  return {
    key: 'song',
    done: true,
    action: addSong,
    nudge: { kind: 'cover', catalogItemUuid: song.catalogItemUuid, accountId: song.accountId },
  };
}

export function onboardingChecklist(facts: OnboardingChecklistFacts): OnboardingChecklist {
  const { song, accountId } = facts;
  const addSong: OnboardingAction = { screen: 'addSong', accountId };
  // The song is always of the checklist's artist; the fallback keeps an older caller safe.
  const factsAccountId = song?.accountId ?? accountId;
  const songPage = (section: ReleaseSongSection): OnboardingAction =>
    song === null
      ? addSong
      : { screen: 'songPage', catalogItemUuid: song.catalogItemUuid, section, accountId: song.accountId };
  const release = (screen: 'releaseTheme' | 'releaseAssets' | 'releasePlan'): OnboardingAction =>
    song === null ? addSong : { screen, catalogItemUuid: song.catalogItemUuid, accountId: song.accountId };

  const items: OnboardingChecklistItem[] = [
    songItem(song, addSong),
    { key: 'audio', done: song !== null && setupItemDone(song.setup, 'master'), action: songPage('files') },
    { key: 'lyrics', done: song !== null && setupItemDone(song.setup, 'lyrics'), action: songPage('lyrics') },
    { key: 'look', done: song !== null && song.lookChosen, action: release('releaseTheme') },
    { key: 'kit', done: song !== null && song.readyKitAssets > 0, action: release('releaseAssets') },
    { key: 'channel', done: facts.connectedChannels > 0, action: { screen: 'channels', accountId: factsAccountId } },
    { key: 'plan', done: song !== null && song.plannedPosts > 0, action: release('releasePlan') },
    {
      key: 'link',
      done: song !== null && setupItemDone(song.setup, 'smartLink') && setupItemDone(song.setup, 'presave'),
      action: songPage('release'),
    },
    { key: 'streams', done: facts.streamSources > 0, action: { screen: 'catalogConnect', accountId: factsAccountId } },
  ];
  if (!isPaidTier(facts.plan)) {
    items.push({ key: 'planPick', done: false, action: { screen: 'plans', accountId } });
  }
  const doneCount = items.filter((item) => item.done).length;
  return { items, doneCount, total: items.length, complete: doneCount === items.length };
}
